/**
 * HOME PAGE DATA, Your Business
 * ──────────────────────────────────────────────────────────────────────────
 * One fetcher for the whole landing page. Mirrors the backend GET
 * /public/content/home aggregate (see backend ContentCtrl `/home`).
 *
 *   DataSourceConfig.home === 'backend' → SystemDataService.getHomeData()
 *   DataSourceConfig.home === 'local'   → the HOME_LOCAL mock below
 *
 * The mock lets you build/design the page with no backend running. The section
 * shapes match content.* in system_data_{lang}.json exactly.
 */
import DataSourceConfig from '../Config/DataSourceConfig';
import SystemDataService from '../Services/SystemDataService';
import { post } from '../Services/ApiService';
import { ServerEndpoint } from '../Redux/endpoints.ts';

const PHONE = '(000) 000-0000';
const EMAIL = 'hello@example.com';

// ── Local fallback (design-time), same shape the backend /home returns ──────
export const HOME_LOCAL = {
    businessInfo: {
        businessName: 'Your Business', phone: PHONE, email: EMAIL,
        website: 'https://www.example.com',
    },
    hero: {
        eyebrow: 'Section Title',
        titleLine1: 'Headline goes here', titleLine2: 'Headline goes here',
        paragraph: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        chips: ['Item One', 'Item Two', 'Item Three', 'Item Four', 'Item Five', 'Item Six'],
        image: '/placeholder.svg', imageAlt: 'Placeholder image',
        stats: [
            { label: 'Item One', sub: 'Add your content here.' },
            { label: 'Item Two', sub: 'Add your content here.' },
            { label: 'Item Three', sub: 'Add your content here.' },
        ],
        primaryCta: { label: 'Get Started', href: '#contact' },
        secondaryCta: { label: `Call ${PHONE}`, href: 'tel:0000000000' },
    },
    whatWeFix: {
        eyebrow: 'Section Title', titleLine1: 'Headline goes here', titleLine2: 'Headline goes here',
        paragraph: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        cards: [
            { icon: '📋', title: 'Item One', text: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.', link: '#services', slug: 'item-one' },
            { icon: '⚠️', title: 'Item Two', text: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.', link: '#services', slug: 'item-two' },
            { icon: '🏗️', title: 'Item Three', text: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.', link: '#services', slug: 'item-three' },
        ],
    },
    whyChooseUs: {
        eyebrow: 'Why Choose Us', titleLine1: 'Headline goes here', titleLine2: 'Headline goes here',
        paragraph: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        image: '/placeholder.svg', imageAlt: 'Placeholder image',
        bullets: ['Add your content here.', 'Add your content here.', 'Add your content here.', 'Add your content here.'],
    },
    process: {
        eyebrow: 'How It Works', titleLine1: 'Headline goes here', titleLine2: 'Headline goes here',
        cta: { label: 'Get Started', href: '#contact' },
        steps: [
            { step: 'STEP 1', title: 'Item One', text: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.' },
            { step: 'STEP 2', title: 'Item Two', text: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.' },
            { step: 'STEP 3', title: 'Item Three', text: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.' },
            { step: 'STEP 4', title: 'Item Four', text: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.' },
        ],
    },
    services: [
        { slug: 'service-one', icon: '✅', cardTitle: 'Service One', cardDescription: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.' },
        { slug: 'service-two', icon: '🧾', cardTitle: 'Service Two', cardDescription: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.' },
        { slug: 'service-three', icon: '🛑', cardTitle: 'Service Three', cardDescription: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.' },
        { slug: 'service-four', icon: '📐', cardTitle: 'Service Four', cardDescription: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.' },
        { slug: 'service-five', icon: '🔍', cardTitle: 'Service Five', cardDescription: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.' },
        { slug: 'service-six', icon: '🔨', cardTitle: 'Service Six', cardDescription: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.' },
    ],
    // NOTE: "Who We Help" (audience) is static, code-owned content, see
    // Public/audience-data.js. It is intentionally not part of the home payload.
    serviceArea: {
        eyebrow: 'Service Area', titleLine1: 'Headline goes here', titleLine2: 'Headline goes here',
        paragraph: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        mapImage: '/placeholder.svg', mapAlt: 'Placeholder image',
        counties: ['Region'],
        cities: [
            { name: 'City One', slug: 'city-one' }, { name: 'City Two', slug: 'city-two' }, { name: 'City Three', slug: 'city-three' },
            { name: 'City Four', slug: 'city-four' }, { name: 'City Five', slug: 'city-five' }, { name: 'City Six', slug: 'city-six' },
            { name: 'City Seven', slug: 'city-seven' }, { name: 'City Eight', slug: 'city-eight' }, { name: 'City Nine', slug: 'city-nine' },
            { name: 'City Ten', slug: 'city-ten' }, { name: 'City Eleven', slug: 'city-eleven' },
        ],
    },
    contact: {
        eyebrow: 'Section Title', heading: 'Headline goes here', headingLine2: 'Headline goes here',
        sub: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        issueTypes: ['Item One', 'Item Two', 'Item Three', 'Item Four', 'Item Five', 'Item Six'],
        submitLabel: 'Send Request', disclaimer: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        phone: PHONE, email: EMAIL, counties: ['Region'],
        responseTime: 'Add your content here.', hours: 'Monday, Saturday',
        asideCta: { icon: '💬', title: 'Section Title', text: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.', cta: { label: 'Contact Us', href: '#contact' } },
        callCta: { icon: '✆', eyebrow: 'Section Title', label: 'Call Now', phone: PHONE, href: 'tel:0000000000' },
    },
    guides: {
        eyebrow: 'Section Title', titleLine1: 'Headline goes here', titleLine2: 'Headline goes here',
        paragraph: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        cta: { label: 'View All Guides', href: '/guides' },
        cards: [
            { category: 'GUIDE', title: 'Guide One', excerpt: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.', image: '/placeholder.svg', slug: 'guide-one' },
            { category: 'GUIDE', title: 'Guide Two', excerpt: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.', image: '/placeholder.svg', slug: 'guide-two' },
            { category: 'GUIDE', title: 'Guide Three', excerpt: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.', image: '/placeholder.svg', slug: 'guide-three' },
            { category: 'GUIDE', title: 'Guide Four', excerpt: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.', image: '/placeholder.svg', slug: 'guide-four' },
        ],
    },
    faq: [
        { id: 'faq-open-permit-sell', question: 'Frequently asked question?', answer: 'Answer goes here.' },
        { id: 'faq-expired-permit', question: 'Frequently asked question?', answer: 'Answer goes here.' },
        { id: 'faq-no-permit-work', question: 'Frequently asked question?', answer: 'Answer goes here.' },
        { id: 'faq-stop-work-order', question: 'Frequently asked question?', answer: 'Answer goes here.' },
    ],
    cta: {
        titleLine1: 'Headline goes here', titleLine2: 'Headline goes here',
        paragraph: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        primaryCta: { label: 'Get Started', href: '#contact' },
        secondaryCta: { label: `Call ${PHONE}`, href: 'tel:0000000000' },
    },
    footer: {
        brand: 'Your Business',
        tagline: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        columns: [
            { title: 'Services', links: [{ label: 'Service One', href: '#services' }, { label: 'Service Two', href: '#services' }, { label: 'Service Three', href: '#services' }, { label: 'Service Four', href: '#services' }] },
            { title: 'Areas', links: [{ label: 'City One', href: '#areas' }, { label: 'City Two', href: '#areas' }, { label: 'City Three', href: '#areas' }] },
            { title: 'Contact', links: [{ label: PHONE, href: 'tel:0000000000' }, { label: EMAIL, href: `mailto:${EMAIL}` }, { label: 'Free Review', href: '#contact' }] },
        ],
        legal: '© 2026 Your Business. All rights reserved.',
        disclaimer: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
    },
};

let HOME_CACHE = null;

/** Fetch the whole home payload. Backend when configured, local mock otherwise; always falls back to the mock on error so the page never renders empty. */
export const getHomeData = async () => {
    if (HOME_CACHE) return HOME_CACHE;
    if (DataSourceConfig.home === 'local') {
        HOME_CACHE = HOME_LOCAL;
        return HOME_CACHE;
    }
    try {
        const data = await SystemDataService.getHomeData();
        HOME_CACHE = data && data.hero ? data : HOME_LOCAL;
        return HOME_CACHE;
    } catch (err) {
        console.error('Failed to load home data from API, using local fallback', err);
        return HOME_LOCAL;
    }
};

/** Reset the in-memory cache (call on language change). */
export const clearHomeCache = () => { HOME_CACHE = null; };

/**
 * Free-review lead submit. Reuses the existing /public/contact intake
 * (email + Zoho lead). address / issueType / note are folded into `message`
 * so toLeadFields (name/phone/message) picks them up.
 */
export const submitFreeReview = async ({ name, phone, address, issueType, note }) => {
    const message = [
        issueType ? `Issue type: ${issueType}` : '',
        address ? `Property address: ${address}` : '',
        note ? `\n${note}` : '',
    ].filter(Boolean).join('\n');

    const payload = {
        name, phone, address, issueType, note, message,
        subject: 'Free Review Request',
        source: 'Homepage Form',
        page_url: typeof window !== 'undefined' ? window.location.href : '',
    };
    const res = await post(ServerEndpoint.CONTACT, payload, true, 'Sending your request…');
    return res?.data;
};
